import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { backgroundQueryRefresh } from "../../../../../../shared/frontend/system/backgroundQueryRefresh";
import { useAuth } from "../auth/useAuth";
import { authQueryScope } from "../query/authQueryScope";
import { areaKey, scopedKey } from "../query/queryKeys";
import { useRealtimeQueryBridge } from "../query/useRealtimeQueryBridge";
import { useGlobalStudioContext } from "../studios/useGlobalStudioContext";

/**
 * Schreibvorgänge, die eine offene Aufgabe erledigen oder anlegen. Nicht jede Aufgabenquelle meldet
 * sich per Realtime (App-Anliegen, Rücklastschriften, manuelle Aufgaben); eigene Änderungen
 * aktualisieren die Zahl deshalb hier, fremde beim nächsten Fensterfokus.
 */
const TASK_WRITE_PATH = /^\/api\/(dashboard\/tasks|studio-app-requests|contract-packages|rest-periods|payments|outbound-messages|inbound-emails)(\/|$|\?)/;

/** Zahl der offenen Aufgaben (ohne Geburtstage) für den Hinweis am Menüeintrag. */
export function useOpenTaskCount(enabled: boolean): number {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { studioIdsQueryParam, appendStudioIdsQuery } = useGlobalStudioContext();
  useRealtimeQueryBridge("tasks", { enabled });
  React.useEffect(() => {
    if (!enabled) return undefined;
    return auth.api.subscribeWrites((path) => {
      if (TASK_WRITE_PATH.test(path)) void queryClient.invalidateQueries({ queryKey: areaKey("tasks") });
    });
  }, [auth.api, enabled, queryClient]);
  const query = useQuery({
    queryKey: scopedKey("tasks", studioIdsQueryParam, "open-count", ...authQueryScope(auth.api, auth.info)),
    queryFn: () => auth.api.get<{ count: number }>(appendStudioIdsQuery("/api/v1/dashboard/tasks/open-count"), { suppressGlobalErrorToast: true }),
    enabled,
    refetchOnWindowFocus: (q) => backgroundQueryRefresh(q),
    retry: false
  });
  // Ein Fehler lässt den Hinweis still weg: Er ist Komfort, die Aufgabenliste bleibt maßgeblich.
  return query.data?.count ?? 0;
}
